<template>
  <div>
    <CCard>
      <CCardHeader class="border-0 px-4 pb-1 pt-4">
        <div class="d-flex align-items-center font-weight-medium">
          <CIcon name="cipGridSm" size="custom-size" :width="18" class="mr-2"/>
          <translate translate-context="accounting.refund">Ledger entries</translate>
        </div>
      </CCardHeader>
      <CCardBody>
        <div class="ledger-entries-table">
          <!-- Filters Row -->
          <CRow v-if="showFilters">
            <CCol md="3" v-if="hasPicaServices">
              <CSelect
                :value.sync="filters.picaServiceId"
                :options="picaServiceOptions"
                size="sm"
              />
            </CCol>
            <CCol md="3" v-if="hasMultipleStakeholders">
              <CSelect
                :value.sync="filters.stakeholderType"
                :options="stakeholderOptions"
                size="sm"
              />
            </CCol>
            <CCol md="3">
              <CSelect
                :value.sync="filters.entryType"
                :options="entryTypeOptions"
                size="sm"
              />
            </CCol>
            <CCol md="3" class="d-flex">
              <CButton
                v-if="hasActiveFilters"
                color="secondary"
                size="sm"
                variant="outline"
                @click="clearFilters"
              >
                <translate translate-context="accounting.ledger">Clear</translate>
              </CButton>
            </CCol>
          </CRow>

          <!-- Main Table -->
          <CDataTable
            :items="filteredEntries"
            :fields="tableFields"
            :bordered="true"
            :striped="true"
            class="custom-table cus custom-table--clean mb-0"
          >
            <template #stakeholder="{item}">
              <td class="text-nowrap">
                <CBadge class="badge-outline mr-1" :color="getStakeholderColor(getStakeholderType(item))">
                  {{ getStakeholderLabel(getStakeholderType(item)) }}
                </CBadge>
                <span v-if="item.stakeholder && item.stakeholder.legal_name">
            {{ item.stakeholder.legal_name }}
          </span>
              </td>
            </template>

            <template #entry_type="{item}">
              <td>
                <CBadge color="light" class="badge-outline">
                  {{ getEntryTypeLabel(item.entry_type) }}
                </CBadge>
              </td>
            </template>

            <template #pica_service="{item}">
              <td>
                <router-link
                  v-if="item.pica_service_id"
                  :to="{ name: 'picaServiceRedirect', params: { picaServiceId: item.pica_service_id }}"
                  class="font-weight-medium"
                >
                  {{ item.pica_service_name || `#${item.pica_service_id}` }}
                </router-link>
                <span v-else>-</span>
              </td>
            </template>

            <template #amount="{item}">
              <td :class="getAmountClass(item.amount)" class="text-right text-nowrap">
                {{ item.amount|currency(item.currency) }}
              </td>
            </template>

            <template #amount_eur="{item}">
              <td :class="getAmountClass(item.amount_eur)" class="text-right text-nowrap">
                <template v-if="item.currency && item.currency !== 'EUR' && item.amount_eur != null">
                  {{ item.amount_eur|currency('EUR') }}
                  <div v-if="item.exchange_rate" class="text-muted small font-weight-normal">
                    <translate translate-context="accounting.ledger">Rate</translate>:
                    {{ formatExchangeRate(item.exchange_rate) }}
                  </div>
                </template>
                <span v-else class="text-muted">-</span>
              </td>
            </template>

            <template #source="{item}">
              <td>
                {{ getSourceLabel(item.source_type) }}
              </td>
            </template>
            <template #description="{item}">
              <td>
                <span style="font-size: 12px">{{ item.description }}</span>
              </td>
            </template>
          </CDataTable>

          <!-- Empty State -->
          <div v-if="filteredEntries.length === 0" class="text-center py-4">
            <CIcon name="cipXCircle" size="xl" class="mb-2"/>
            <p class="mb-0">
              <translate translate-context="accounting.ledger">No ledger entries found</translate>
            </p>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import {
  STAKEHOLDER_TYPE_LABELS,
  STAKEHOLDER_TYPE_COLORS,
  ENTRY_TYPE_LABELS,
  SOURCE_TYPE_LABELS
} from '../constants'
export default {
  name: 'LedgerEntriesTable',
  props: {
    entries: {
      type: Array,
      default: () => []
    },
    showFilters: {
      type: Boolean,
      default: true
    },
    showPicaServiceColumn: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      filters: {
        picaServiceId: '',
        stakeholderType: '',
        entryType: ''
      }
    }
  },
  computed: {
    tableFields() {
      const fields = []

      if (this.showPicaServiceColumn && this.hasPicaServices) {
        fields.push({key: 'pica_service', label: this.$pgettext('accounting.ledger', 'Event')})
      }

      fields.push(
        {key: 'stakeholder', label: this.$pgettext('accounting.ledger', 'Stakeholder')},
        {key: 'entry_type', label: this.$pgettext('accounting.ledger', 'Type')},
        {key: 'amount', label: this.$pgettext('accounting.ledger', 'Amount'), _classes: 'text-right'},
      )

      if (this.hasNonEurCurrency) {
        fields.push({
          key: 'amount_eur',
          label: this.$pgettext('accounting.ledger', 'Amount (EUR)'),
          _classes: 'text-right'
        })
      }

      fields.push(
        {key: 'source', label: this.$pgettext('accounting.ledger', 'Source')},
        {key: 'description', label: this.$pgettext('accounting.ledger', 'Description')}
      )

      return fields
    },

    hasPicaServices() {
      return this.entries.some(e => e.pica_service_id)
    },

    hasNonEurCurrency() {
      return this.entries.some(e => e.currency && e.currency !== 'EUR')
    },

    hasMultipleStakeholders() {
      const types = new Set(this.entries.map(e => e.stakeholder ? e.stakeholder.stakeholder_type : null))
      return types.size > 1
    },

    picaServiceOptions() {
      const services = []
      const seen = new Set()
      for (const entry of this.entries) {
        if (entry.pica_service_id && !seen.has(entry.pica_service_id)) {
          seen.add(entry.pica_service_id)
          services.push({
            value: entry.pica_service_id,
            label: entry.pica_service_name || `PicaService #${entry.pica_service_id}`
          })
        }
      }
      return [
        {value: '', label: this.$pgettext('accounting.ledger', 'All Events')},
        ...services
      ]
    },

    stakeholderOptions() {
      const types = new Set(this.entries.map(e => e.stakeholder ? e.stakeholder.stakeholder_type : null).filter(Boolean))
      return [
        {value: '', label: this.$pgettext('accounting.ledger', 'All Stakeholders')},
        ...[...types].map(type => ({
          value: type,
          label: this.getStakeholderLabel(type)
        }))
      ]
    },

    entryTypeOptions() {
      const types = new Set(this.entries.map(e => e.entry_type).filter(Boolean))
      return [
        {value: '', label: this.$pgettext('accounting.ledger', 'All Types')},
        ...[...types].map(type => ({
          value: type,
          label: this.getEntryTypeLabel(type)
        }))
      ]
    },

    hasActiveFilters() {
      return this.filters.picaServiceId || this.filters.stakeholderType || this.filters.entryType
    },

    filteredEntries() {
      return this.entries.filter(entry => {
        if (this.filters.picaServiceId && entry.pica_service_id !== this.filters.picaServiceId) {
          return false
        }
        if (this.filters.stakeholderType && this.getStakeholderType(entry) !== this.filters.stakeholderType) {
          return false
        }
        if (this.filters.entryType && entry.entry_type !== this.filters.entryType) {
          return false
        }
        return true
      })
    },

  },
  methods: {
    clearFilters() {
      this.filters = {
        picaServiceId: '',
        stakeholderType: '',
        entryType: ''
      }
    },
    getStakeholderType(entry) {
      return entry.stakeholder ? entry.stakeholder.stakeholder_type : null
    },
    getStakeholderLabel(type) {
      return STAKEHOLDER_TYPE_LABELS[type] || type || '-'
    },
    getStakeholderColor(type) {
      return STAKEHOLDER_TYPE_COLORS[type] || 'secondary'
    },
    getEntryTypeLabel(type) {
      return ENTRY_TYPE_LABELS[type] || type
    },
    getSourceLabel(type) {
      return SOURCE_TYPE_LABELS[type] || type
    },
    getAmountClass(amount) {
      const num = parseFloat(amount)
      if (num > 0) return 'success'
      if (num < 0) return 'warning'
      return ''
    },
    formatAmount(amount) {
      const num = parseFloat(amount) || 0
      const sign = num > 0 ? '+' : ''
      return sign + num.toFixed(2)
    },
    formatExchangeRate(rate) {
      const num = parseFloat(rate)
      if (!num || isNaN(num)) return ''
      return num.toFixed(4)
    },
  }
}
</script>

